import React, { useCallback, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import toast from "react-hot-toast";

import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Switch } from "@/components/ui/Switch";
import SearchInput from "@/components/shared/SearchInput";
import { DataTableNew } from "@/components/shared/DataTable";
import CalendarTypeService from "@/services/calendarTypeService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { formatDate } from "@/utils/dateUtils";
import AddCalendarTypeModal from "./AddCalendarTypeModal";

/**
 * Tenant calendar types (e.g. "India Calendar"), embedded as a card inside
 * the existing Organization Settings page — a structural sibling of
 * ShiftTimingSettingsCard, reusing the page's TENANT_CONFIG_EDIT-derived
 * `canEdit` the same way (UX only; the backend enforces it independently).
 *
 * Unlike Shift Timing's fetch-all client-side table, this one uses
 * DataTableNew against the real paginated /calendar-type/list endpoint,
 * since a tenant can plausibly add one calendar per country.
 *
 * Edit here is toggle-only — the Status switch is the entire edit surface,
 * there is no rename UI.
 */
const CalendarTypeSettingsCard = ({ canEdit }) => {
  const [search, setSearch] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const tableRef = useRef(null);

  const triggerRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // DataTableNew expects its `api` to resolve `{ data: <body> }`;
  // CalendarTypeService.list() resolves the response body directly (same
  // convention as ShiftTimingService), so wrap it here.
  const fetchForTable = useCallback(async (params) => ({ data: await CalendarTypeService.list(params) }), []);

  const handleToggleActive = async (calendar) => {
    const newStatus = !calendar.isActive;
    // Optimistic flip: the switch and the toast agree immediately, instead
    // of waiting on the `refreshKey` remount to reflect the change.
    tableRef.current?.patchRow(calendar.id, { isActive: newStatus });
    try {
      setSavingId(calendar.id);
      await CalendarTypeService.update(calendar.id, { isActive: newStatus });
      toast.success(calendar.isActive ? "Calendar disabled." : "Calendar enabled.");
      triggerRefresh();
    } catch (err) {
      tableRef.current?.patchRow(calendar.id, { isActive: calendar.isActive });
      toast.error(apiErrorMessageFormat(err, "Failed to update calendar."));
    } finally {
      setSavingId(null);
    }
  };

  const columns = useMemo(
    () => [
      { key: "name", label: "Name", sortable: true, className: "font-medium" },
      {
        key: "isActive",
        label: "Status",
        sortable: true,
        sortKey: "is_active",
        render: (_value, calendar) => (
          <Switch
            checked={calendar.isActive}
            disabled={!canEdit || savingId !== null}
            readOnly={!canEdit}
            label={`${calendar.name} active`}
            onToggle={() => handleToggleActive(calendar)}
          />
        ),
      },
      {
        key: "createdAt",
        label: "Created",
        sortable: true,
        sortKey: "created_at",
        render: (value) => formatDate(value),
      },
    ],
    // handleToggleActive is re-created every render; the columns only need to
    // be rebuilt when what they render changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, savingId],
  );

  const handleSaved = () => {
    toast.success("Calendar created.");
    setShowAddModal(false);
    triggerRefresh();
  };

  return (
    <Card className="p-6 bg-background-secondary border border-border-primary rounded-xl">
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1.5">
            <h2 className="text-lg font-semibold text-text-primary">Calendar Type</h2>
            <p className="text-[13px] text-text-secondary leading-relaxed">
              Define named calendars (e.g. by country) jobs can be tagged with. Disabling a calendar hides it from new job selections without affecting jobs that already
              use it.
            </p>
          </div>
          {canEdit && (
            <Button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm bg-button-secondary text-text-base"
            >
              <Plus size={14} />
              Add Calendar
            </Button>
          )}
        </div>

        <div className="max-w-xs">
          <SearchInput value={search} onChange={setSearch} placeholder="Search calendars..." />
        </div>

        <DataTableNew
          ref={tableRef}
          key={`calendar-type-list-${refreshKey}`}
          columns={columns}
          api={fetchForTable}
          dataPath="data"
          search={search}
          paginationLabel="calendars"
          itemSize={10}
          defaultSort={{ key: "created_at", order: "desc" }}
        />
      </div>

      {showAddModal && <AddCalendarTypeModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onSaved={handleSaved} />}
    </Card>
  );
};

export default CalendarTypeSettingsCard;
